// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_MESSAGES, VIOLATION_ERROR_MESSAGES } from '@/api/constants';
import {
  ConflictingAsset,
  ErrorCode,
  ExistingAsset,
  ExistingItem,
  ProviderError,
  WellFormedError,
  errorResponseSchema,
} from '@/api/types';
import { captureException, captureMessage } from '@sentry/react';
import { AxiosError } from 'axios';

/**
 * Type guard to check if a string is a valid ErrorCode with a message mapping
 */
function hasErrorMessage(errorType: string): errorType is ErrorCode {
  return errorType in ERROR_MESSAGES;
}

/**
 * Returns a human-readable string for a violation error if the
 * apiStatusCode matches a known ViolationMap code (5000-5100). The
 * {value} placeholder in the template is replaced with entityValue
 * when present; if no value is on the error, the placeholder (and
 * any leading space) is stripped to keep the sentence grammatical.
 *
 * Returns undefined for non-violation errors so callers can fall
 * back to their own copy or the existing inline form handlers.
 */
export function getHumanFormattedError(
  error: WellFormedError
): string | undefined {
  const template = VIOLATION_ERROR_MESSAGES[error.apiStatusCode];
  if (!template) {
    return undefined;
  }
  const value = error.entityValue?.trim();
  if (value) {
    return template.replace('{value}', value);
  }
  return template.replace(' {value}', '').replace('{value}', '');
}

/**
 * Transform any error into a well-formed error structure
 * This also dispatches sentry errors/messages based on the error type and status code.
 * @param error - The base error to transform to our well known error structure
 */
export function transformAndDispatchError(error: AxiosError): WellFormedError {
  const timestamp = new Date();
  const sentToSentry = false;
  try {
    // Handle network errors (no response from server)
    if (!error.response) {
      const networkError: WellFormedError = {
        errorType: 'NETWORK_ERROR',
        message:
          'Unable to connect to the server. Please check your internet connection and try again.',
        httpStatusCode: 0,
        apiStatusCode: 0,
        sentToSentry: false,
        timestamp,
        isNetworkError: true,
      };

      // Send network errors to Sentry if possible
      try {
        captureException(error);
        networkError.sentToSentry = true;
      } catch {
        // Ignore Sentry errors
      }

      return networkError;
    }

    // Handle HTTP errors with response

    const httpStatusCode = error.response.status;

    // Try to parse the error response
    const result = errorResponseSchema.safeParse(error.response.data);

    let errorType: string;
    let message: string;
    let apiStatusCode: number;

    if (result.success && result.data.metadata.errors.length > 0) {
      const firstError = result.data.metadata.errors[0];
      errorType = firstError.type;
      message = hasErrorMessage(firstError.type)
        ? ERROR_MESSAGES[firstError.type]
        : firstError.message;
      apiStatusCode = firstError.statusCode;
    } else {
      errorType = `HTTP_${httpStatusCode.toString()}`;
      apiStatusCode = httpStatusCode;
      message = `Server encountered an unexpected error`;
    }

    // Extract entity details from first error if available (for unique violations)
    let entityColumnId: string | undefined;
    let entityType: string | undefined;
    let entityValue: string | undefined;
    let conflictingAssets: ConflictingAsset[] | undefined;
    let providerErrors: ProviderError[] | undefined;
    let existingAsset: ExistingAsset | undefined;
    let existingItem: ExistingItem | undefined;
    if (result.success && result.data.metadata.errors.length > 0) {
      const firstError = result.data.metadata.errors[0];
      if ('conflictingAssets' in firstError) {
        conflictingAssets = firstError.conflictingAssets;
      }
      if ('providerErrors' in firstError) {
        providerErrors = firstError.providerErrors;
      }
      if ('existingAsset' in firstError) {
        existingAsset = firstError.existingAsset;
      }

      if ('existingItem' in firstError) {
        existingItem = firstError.existingItem;
      }

      if ('entityColumnId' in firstError) {
        entityColumnId = firstError.entityColumnId;
      }
      if ('entityType' in firstError) {
        entityType = firstError.entityType;
      }
      if ('entityValue' in firstError) {
        entityValue = firstError.entityValue;
      }
    }

    const wellFormedError: WellFormedError = {
      errorType,
      message,
      httpStatusCode,
      sentToSentry,
      timestamp,
      apiStatusCode,
      isNetworkError: false,
      entityColumnId,
      entityType,
      entityValue,
      conflictingAssets,
      existingAsset,
      existingItem,
      providerErrors,
    };

    // Handle Sentry reporting based on status code and configuration
    try {
      if (httpStatusCode >= 500) {
        // 500+ errors always go to Sentry as exceptions
        captureException(error);
        wellFormedError.sentToSentry = true;
      } else if (httpStatusCode === 401 || httpStatusCode === 403) {
        // Auth errors go to Sentry as messages (not exceptions)
        const request = error.request as XMLHttpRequest | undefined;
        captureMessage(
          `Authentication error: ${httpStatusCode.toString()} on ${request?.responseURL ?? 'unknown'}`,
          'info'
        );
        wellFormedError.sentToSentry = true;
      }
    } catch {
      // Ignore errors from sentry that may occur during reporting, we can't do anything about it
    }

    return wellFormedError;
  } catch (_error) {
    // Handle a bad decode for the error response
    return {
      errorType: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred',
      httpStatusCode: 0,
      apiStatusCode: 0,
      sentToSentry,
      timestamp,
      isNetworkError: false,
    };
  }
}
